import { useState } from 'react'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import PropTypes from 'prop-types'

import { categoryIcon, formatDollarCents, formatPercent, barWidth, NO_BALANCE_NOTE } from './shared'

// The bar sits in a fixed-width track pinned to the right edge of the card, so
// the "$14,320.43 • 16%" value has the left of the row to itself. Tune
// TRACK_WIDTH to trade bar length against label room.
const TRACK_WIDTH = 40

// "$14,320.43 • 16%" — dollars first, then percent, per the mobile design.
// `dollarless` drops the dollar figure (percent only) for a zero-balance account
// where every amount would be "$0".
const metricLabel = (value, dollarless) => {
  if (!value) {
    return ''
  }
  const parts = []
  if (!dollarless && value.dollar !== null && value.dollar !== undefined) {
    parts.push(formatDollarCents(value.dollar))
  }
  if (value.percent !== null && value.percent !== undefined) {
    parts.push(formatPercent(value.percent))
  }
  return parts.join(' • ')
}

// "Overall • N accounts" for the aggregate view, or the account name for a
// specific account. Supplied by the caller. Allowed to wrap to two lines, then
// truncated with an ellipsis (per the mobile account design).
const Title = styled.div`
  color: ${p => p.theme.darkestGray};
  font-size: 16px;
  text-align: center;
  margin-bottom: 20px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

// Sub-heading under the title for a zero-balance account: the cards below show
// the percentage-only mix to use once the account is funded.
const Note = styled.div`
  font-size: 13px;
  text-align: center;
  color: ${p => p.theme.darkestGray};
  margin-bottom: 20px;
`

// Current vs recommended legend, centered above the cards (the desktop layout
// shows its own column headers instead). A grey dot for the current portfolio
// and a blue dot for the plan one, matching the bar colors below.
const Legend = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 25px;
  margin-bottom: 20px;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
  font-size: 14px;
  color: ${p => p.color || p.theme.mediumGray};
`

const Swatch = styled.span`
  width: 18px;
  height: 18px;
  flex: 0 0 18px;
  background-color: ${p => p.color};
  border-radius: 50%;
`

const Categories = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 20px;
`

// Each asset class is its own bordered card. On an account view the whole card
// is a toggle that expands to show the holdings, so it is `position: relative`
// to anchor the caret in the top-right corner.
const Category = styled.div`
  position: relative;
  border: solid 2px ${p => p.theme.primaryLighter};
  border-radius: 10px;
  padding: 18px 0 25px 10px;
  cursor: ${p => (p.expandable ? 'pointer' : 'default')};
`

const Header = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
`

const IconCell = styled.div`
  width: 30px;
  height: 30px;
  flex: 0 0 30px;
`

const CategoryName = styled.div`
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${p => p.theme.darkestGray};
`

// Anchored to the card's top-right corner (20px down, 10px in). Rotates to
// point up when the card is expanded.
const Caret = styled.svg`
  position: absolute;
  top: 20px;
  right: 10px;
  transition: transform 0.2s ease;
  transform: rotate(${p => (p.open ? '180deg' : '0deg')});
`

const Metrics = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  row-gap: 8px;
`

const MetricRow = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
`

// Track hugs the right edge of the card; the fill is right-aligned within it so
// the bars line up against the container's right side.
const Track = styled.div`
  flex: 0 0 ${TRACK_WIDTH}%;
  display: flex;
  height: 14px;
`

const Fill = styled.div`
  height: 100%;
  margin-left: auto;
  width: ${p => p.width}%;
  background-color: ${p => p.color};
`

const Value = styled.div`
  flex: 1 1 auto;
  white-space: nowrap;
  font-size: ${p => (p.small ? '15px' : '18px')};
  font-weight: ${p => (p.bold ? 600 : 'normal')};
  color: ${p => p.color};
`

const Investments = styled.div`
  /* Cancel the card's 10px left padding so the divider reaches the card edge,
     then restore it as padding so the holdings stay aligned with the rows above. */
  margin-top: 18px;
  margin-left: -10px;
  padding-top: 6px;
  padding-left: 10px;
  border-top: 2px solid ${p => p.theme.primaryLighter};
`

const InvestmentName = styled.div`
  margin-top: 30px;
  font-size: 14px;
  color: ${p => p.theme.mediumGray};
`

const Metric = ({ value, color, bold, small, showBar, dollarless }) =>
  value ? (
    <MetricRow>
      <Value color={color} bold={bold} small={small}>
        {metricLabel(value, dollarless)}
      </Value>
      {showBar && (
        <Track>
          <Fill width={barWidth(value)} color={color} />
        </Track>
      )}
    </MetricRow>
  ) : null

// Current values in grey, recommended in the brand blue and bold. `small` is
// used for the holdings inside an expanded card (15px vs the 18px category row).
// Only the category row shows bars; individual holdings omit them (showBar).
// When `zeroBalance`, the account has no current side, so drop the current
// metric — even if the breakdown sends a 0% / $0 current entry rather than null —
// and show recommended as percent only.
const AllocationBlock = ({ current, recommended, theme, small, showBar = true, zeroBalance }) => (
  <Metrics>
    {!zeroBalance && (
      <Metric value={current} color={theme.lightestGray} small={small} showBar={showBar} />
    )}
    <Metric
      value={recommended}
      color={theme.primaryColor}
      bold
      small={small}
      showBar={showBar}
      dollarless={zeroBalance}
    />
  </Metrics>
)

// Show zero for null holding values so current and recommended can still be compared.
const ZERO_VALUE = { dollar: 0, percent: 0 }

const HoldingAllocation = ({ current, recommended, theme, zeroBalance }) => {
  const missingValue = zeroBalance ? null : ZERO_VALUE

  return (
    <AllocationBlock
      current={current ?? missingValue}
      recommended={recommended ?? missingValue}
      theme={theme}
      small
      showBar={false}
      zeroBalance={zeroBalance}
    />
  )
}

const MobileAssetAllocation = ({
  categories,
  expandable,
  title,
  zeroBalance,
  isModified,
  className,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(() => new Set())

  const toggle = name =>
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(name)) {
        next.delete(name)
      } else {
        next.add(name)
      }
      return next
    })

  return (
    <div className={className}>
      {title && <Title theme={theme}>{title}</Title>}

      {/* Zero-balance accounts have nothing invested yet, so the cards below are
          the percentage-only mix to use once the account is funded. */}
      {zeroBalance && <Note theme={theme}>{NO_BALANCE_NOTE}</Note>}

      <Legend theme={theme}>
        {/* Zero-balance: there's no current allocation, so drop its legend item. */}
        {!zeroBalance && (
          <LegendItem theme={theme}>
            <Swatch color={theme.lightestGray} />
            Current
          </LegendItem>
        )}
        <LegendItem theme={theme} color={theme.primaryColor}>
          <Swatch color={theme.primaryColor} />
          {isModified ? 'Your changes' : 'Recommended'}
        </LegendItem>
      </Legend>

      <Categories>
        {categories.map(category => {
          const hasInvestments = (category.investments || []).length > 0
          const cardExpandable = expandable && hasInvestments
          const isOpen = expanded.has(category.name)

          return (
            <Category
              key={category.name}
              expandable={cardExpandable}
              role={cardExpandable ? 'button' : undefined}
              tabIndex={cardExpandable ? 0 : undefined}
              aria-expanded={cardExpandable ? isOpen : undefined}
              onClick={cardExpandable ? () => toggle(category.name) : undefined}
              onKeyDown={
                cardExpandable
                  ? e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        toggle(category.name)
                      }
                    }
                  : undefined
              }>
              {cardExpandable && (
                // viewBox is tight to the chevron so `top: 20px` / `right: 10px`
                // place the visible glyph, not a padded box, at those offsets.
                <Caret open={isOpen} width='20' height='12' viewBox='0 0 20 12' aria-hidden='true'>
                  <path
                    d='M2 2 L10 10 L18 2'
                    fill='none'
                    stroke={theme.addButtonDisabledTextColor}
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </Caret>
              )}
              <Header>
                <IconCell>{categoryIcon(category.name)}</IconCell>
                <CategoryName>{category.name}</CategoryName>
              </Header>

              <AllocationBlock
                current={category.current}
                recommended={category.recommended}
                theme={theme}
                zeroBalance={zeroBalance}
              />

              {cardExpandable && isOpen && (
                <Investments>
                  {category.investments.map(investment => (
                    <div key={investment.name}>
                      <InvestmentName>{investment.name}</InvestmentName>
                      <HoldingAllocation
                        current={investment.current}
                        recommended={investment.recommended}
                        theme={theme}
                        zeroBalance={zeroBalance}
                      />
                    </div>
                  ))}
                </Investments>
              )}
            </Category>
          )
        })}
      </Categories>
    </div>
  )
}

const valueShape = PropTypes.shape({
  percent: PropTypes.number,
  dollar: PropTypes.number,
})

MobileAssetAllocation.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      current: valueShape,
      recommended: valueShape,
      investments: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          current: valueShape,
          recommended: valueShape,
        })
      ),
    })
  ).isRequired,
  /** When true (account views), each card can expand to reveal its holdings. */
  expandable: PropTypes.bool,
  /** Heading above the cards, e.g. "Overall • 5 accounts" or an account name. */
  title: PropTypes.string,
  /** Zero-balance account: percentages only, no Current legend, plus the note. */
  zeroBalance: PropTypes.bool,
  /** Relabels the plan legend item as "Your changes" when displaying a modified case. */
  isModified: PropTypes.bool,
  className: PropTypes.string,
}

export default MobileAssetAllocation
